function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { composeValidators, maxLengthValidation, noHtmlPatternValidation, requiredValidation } from 'validators';
import { Checkbox, Spinner, Callout, ConfirmationModal, TextField, Typography } from 'seagull';
import { getCustomMessageValidation } from "../../business-logic/validators";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { useStoreActions } from "../../contexts";
import { relationshipModel } from "../../models";
import { helpArticles } from "../../utils/helpArticles";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import { useRestfulWrapper } from "../useRestfulWrapper";
import styles from "./AgreementTerminationModal.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const emailContentLabel = 'Email content';
const emailSubjectLabel = 'Email subject';
const emailContentRules = getCustomMessageValidation(emailContentLabel, true);
const emailSubjectRules = composeValidators([requiredValidation('Required'), maxLengthValidation(120, emailSubjectLabel), noHtmlPatternValidation(emailSubjectLabel)]);
function getDefaultEmailContent(buyerFirstName) {
  return "Dear ".concat(buyerFirstName, ",\n\nI am writing to formally notify you that, as per our previous discussions, I am terminating the agreement between us effective immediately.\n\nPlease note that all outstanding tasks and responsibilities have been completed to the best of our ability, and we're available to provide any necessary assistance during this transition period.\n\nWe sincerely appreciate the opportunity to have worked with you and wish you all the best in your future endeavors.");
}
function getDefaultEmailSubject(vendorName) {
  return "Your agreement with ".concat(vendorName, " was terminated");
}
function validateCustomerName(value, relationship) {
  const isValid = relationshipModel.customerName(relationship).trim().toLowerCase() === value.trim().toLowerCase();
  return isValid || "Company name doesn't match";
}
export function AgreementTerminationModal(props) {
  const {
    relationshipId,
    dismissModal
  } = props;
  const {
    api
  } = useStoreActions();
  const [notifyClient, setNotifyClient] = useState(false);
  const {
    control,
    setValue,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: {
      companyName: '',
      emailSubject: '',
      emailContent: ''
    }
  });
  const {
    data,
    loading,
    renderRestfulState
  } = useRestfulWrapper({
    fetch: () => api.relationship.get(relationshipId)
  });
  useEffect(() => {
    if (data) {
      setValue('emailSubject', getDefaultEmailSubject(relationshipModel.vendorName(data)));
      setValue('emailContent', getDefaultEmailContent(relationshipModel.buyerFirstName(data) || ''));
    }
  }, [data]);
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    title: "Terminate agreement",
    skin: "danger",
    textAlignCenter: false,
    primaryButtonLabel: notifyClient ? 'Terminate and notify client' : 'Terminate',
    primaryButtonDisabled: loading,
    secondaryButtonLabel: "Cancel",
    onPrimaryButtonClick: handleSubmit(_ref => {
      let {
        emailSubject,
        emailContent
      } = _ref;
      return dismissModal({
        confirmed: true,
        notification: notifyClient ? {
          subject: emailSubject,
          message: emailContent
        } : undefined
      });
    }),
    onSecondaryButtonClick: () => dismissModal({
      confirmed: false
    }),
    content: renderRestfulState({
      renderLoader: () => /*#__PURE__*/_jsx("div", {
        className: styles.loading,
        children: /*#__PURE__*/_jsx(Spinner, {})
      }),
      renderChild: _ref2 => {
        let {
          data: relationship
        } = _ref2;
        return /*#__PURE__*/_jsxs("div", {
          className: styles.modalContentWrapper,
          children: [/*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsxs(Typography, {
              children: ["Terminating the agreement with", ' ', /*#__PURE__*/_jsx(Typography, {
                className: styles.customerName,
                as: "span",
                weight: "bold",
                children: relationshipModel.customerName(relationship)
              }), ' ', "will immediately stop new invoices issuance and the initiation of payments for pending invoices."]
            }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Typography, {
              children: "Note that this does not stop payments already in process."
            }), /*#__PURE__*/_jsx(Typography, {
              children: "Please ensure all final invoices are settled before proceeding, as terminated agreements cannot be reactivated."
            })]
          }), /*#__PURE__*/_jsx(Controller, {
            name: "companyName",
            control: control,
            rules: {
              validate: value => validateCustomerName(value, relationship)
            },
            render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
              collapseHelperText: false
            }, connectControllerToTextField(p)), {}, {
              label: "Confirm by entering client's company name",
              testId: "company-name-verification"
            }))
          }), /*#__PURE__*/_jsxs(Callout, {
            children: ["An existing agreement can be edited to change the scope of work or terms.", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
              openInNewTab: true,
              href: helpArticles.howToEditAgreement(),
              children: "Learn more about editing an agreement"
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.notificationEmailFields,
            children: [/*#__PURE__*/_jsx(Checkbox, {
              testId: "toggle-notify",
              onChange: () => setNotifyClient(prev => !prev),
              children: /*#__PURE__*/_jsx(Typography, {
                children: "Notify my client of this action"
              })
            }), notifyClient && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Controller, {
                rules: emailSubjectRules,
                control: control,
                name: "emailSubject",
                render: p => /*#__PURE__*/_jsx(TextField, _objectSpread({
                  collapseHelperText: false,
                  label: emailSubjectLabel,
                  testId: "email-subject"
                }, connectControllerToTextField(p, {
                  alwaysShowErrors: true
                })))
              }), /*#__PURE__*/_jsx(Controller, {
                rules: emailContentRules,
                control: control,
                name: "emailContent",
                render: p => /*#__PURE__*/_jsx(TextField, _objectSpread({
                  collapseHelperText: false,
                  multiline: true,
                  minRows: 10,
                  maxRows: 10,
                  label: emailContentLabel,
                  testId: "email-content"
                }, connectControllerToTextField(p)))
              })]
            })]
          })]
        });
      }
    })
  });
}